import { z } from 'zod';
import { AnlagenStatus, AnlagenTyp, Durchfuehrender, FaelligkeitStatus, PruefErgebnis, } from '../enums.js';
/**
 * Anlage — ein brandschutztechnisches Gerät oder Bauteil in einer Liegenschaft.
 *
 * „Jede Brandschutzklappe, jeder Feuerlöscher hat eine eigene Unterlage." Das
 * ist der Gegenstand, an dem Prüffristen, Mängel und Nachweise hängen. Ohne
 * ihn gäbe es nur ein Objekt und eine Sammlung von Dateien.
 */
export const anlage = z.object({
    id: z.number().int().positive(),
    liegenschaftId: z.number().int().positive(),
    typ: AnlagenTyp,
    /** Sprechende Bezeichnung, z. B. „PG6 Flur EG links". */
    bezeichnung: z.string(),
    hersteller: z.string().nullable().optional(),
    modell: z.string().nullable().optional(),
    /** Seriennummer des Geräts — bei der Versicherungsprüfung das Suchkriterium. */
    seriennummer: z.string().nullable().optional(),
    /** Wo im Objekt: „Treppenhaus 2. OG", „Technikraum". */
    standortImObjekt: z.string().nullable().optional(),
    einbauDatum: z.string().datetime().nullable().optional(),
    status: AnlagenStatus,
    notizen: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createAnlageRequest = anlage
    .omit({ id: true, createdAt: true, updatedAt: true })
    .extend({
    bezeichnung: z.string().trim().min(1).max(200),
    hersteller: z.string().trim().max(120).nullable().optional(),
    modell: z.string().trim().max(120).nullable().optional(),
    seriennummer: z.string().trim().max(120).nullable().optional(),
    standortImObjekt: z.string().trim().max(200).nullable().optional(),
    status: AnlagenStatus.default('aktiv'),
    notizen: z.string().max(5000).nullable().optional(),
});
/** Die Liegenschaft ist beim Ändern raus — eine Anlage wandert nicht ins andere Objekt. */
export const updateAnlageRequest = createAnlageRequest.omit({ liegenschaftId: true }).partial();
export const ANLAGE_SORT_FIELDS = ['bezeichnung', 'typ', 'status', 'einbauDatum'];
export const anlageListQuery = z.object({
    liegenschaftId: z.coerce.number().int().positive().optional(),
    customerId: z.coerce.number().int().positive().optional(),
    typ: AnlagenTyp.optional(),
    status: AnlagenStatus.optional(),
    search: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((v) => (v ? v : undefined)),
    sort: z
        .string()
        .trim()
        .max(40)
        .optional()
        .transform((v) => (v ? v : undefined)),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Abweichender Prüfzyklus für eine einzelne Anlage.
 *
 * Nötig, weil die Norm nicht alles regelt: Bei Sprinkler- und Löschanlagen
 * kommt die Frist aus dem Regelwerk des Sachversicherers, bei Brandschutz-
 * klappen aus der Zulassung des Fabrikats. Ohne Übersteuerung müsste der
 * Betrieb entweder mit einer falschen Frist leben oder die Anlage gar nicht
 * führen.
 */
export const anlageZyklusOverride = z.object({
    id: z.number().int().positive(),
    anlageId: z.number().int().positive(),
    /** Zyklus-Code aus `pruefzyklen.json`, z. B. `wartung`. */
    zyklusCode: z.string(),
    intervallMonate: z.number().int().positive().max(600),
    /** Warum abgewichen wird — Pflicht, damit die Abweichung nachvollziehbar bleibt. */
    begruendung: z.string(),
    createdAt: z.string().datetime(),
});
export const setZyklusOverrideRequest = z.object({
    zyklusCode: z.string().trim().min(1).max(60),
    intervallMonate: z.number().int().positive().max(600),
    begruendung: z.string().trim().min(3).max(1000),
});
/**
 * Eine durchgeführte Prüfung.
 *
 * Sie ist der Anker der nächsten Fälligkeit — deshalb ist `durchgefuehrtAm`
 * Pflicht und darf nicht in der Zukunft liegen.
 */
export const pruefung = z.object({
    id: z.number().int().positive(),
    anlageId: z.number().int().positive(),
    zyklusCode: z.string(),
    durchgefuehrtAm: z.string().datetime(),
    durchfuehrender: Durchfuehrender,
    /** Wer geprüft hat — als Benutzer oder, bei Fremdfirmen, als Freitext. */
    pruefendeUserId: z.number().int().positive().nullable().optional(),
    pruefendeExtern: z.string().nullable().optional(),
    ergebnis: PruefErgebnis,
    notiz: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
});
export const createPruefungRequest = pruefung
    .omit({ id: true, createdAt: true, anlageId: true })
    .extend({
    zyklusCode: z.string().trim().min(1).max(60),
    pruefendeExtern: z.string().trim().max(200).nullable().optional(),
    notiz: z.string().max(5000).nullable().optional(),
});
/**
 * Fälligkeitszeile, wie sie die Oberfläche zeigt.
 *
 * `faelligAm === null` ist ein echter Zustand, kein Fehler: Für manche
 * Anlagenarten ist bei uns keine allgemeine Frist hinterlegt, und ohne
 * Einbaudatum gibt es bei nie geprüften Anlagen keinen Anker. `grund` sagt,
 * welcher der beiden Fälle vorliegt.
 */
export const faelligkeitZeile = z.object({
    anlageId: z.number().int().positive(),
    zyklusCode: z.string(),
    bezeichnung: z.string(),
    durchfuehrender: Durchfuehrender,
    intervallMonate: z.number().int().nullable(),
    /** Gesetzt, wenn das Intervall von der Vorgabe abweicht. */
    uebersteuert: z.boolean(),
    grundlage: z.string().nullable(),
    hinweis: z.string().nullable(),
    letztePruefungAm: z.string().datetime().nullable(),
    faelligAm: z.string().datetime().nullable(),
    grund: z.enum(['nach_pruefung', 'nach_einbau', 'kein_intervall', 'kein_anker']),
    /** Negativ = überfällig. `null`, wenn keine Fälligkeit berechenbar ist. */
    tageBisFaellig: z.number().int().nullable(),
    /**
     * Dringlichkeit, vom Server bewertet — die Schwelle für „bald fällig" ist
     * dort konfiguriert; die Oberfläche rechnet sie nicht nach.
     */
    faelligkeitStatus: FaelligkeitStatus,
});
